const PREVIEW_PATH_PREFIX = "/storefront-preview";
const PREVIEW_MESSAGE = "vbs-storefront-preview";

let previewState = {
  active: false,
  embed: false,
  ready: false,
  jwt: "",
  companyKey: "",
  locale: "en-US",
  tenantOverride: null,
  listeners: new Set(),
};

function notifyListeners() {
  previewState.listeners.forEach((listener) => {
    try {
      listener(previewState);
    } catch {
      // ignore listener errors
    }
  });
}

export function isStorefrontPreviewPath(pathname = "") {
  const path = String(pathname || "");
  return path === PREVIEW_PATH_PREFIX || path.startsWith(`${PREVIEW_PATH_PREFIX}/`);
}

export function isStorefrontPreviewActive() {
  return previewState.active;
}

export function isStorefrontPreviewEmbed() {
  return previewState.embed;
}

export function getStorefrontPreviewJwt() {
  return previewState.jwt || "";
}

export function getStorefrontPreviewCompanyKey() {
  return previewState.companyKey || "";
}

export function getStorefrontPreviewLocale() {
  return previewState.locale || "en-US";
}

export function getStorefrontPreviewTenantOverride() {
  return previewState.tenantOverride;
}

export function subscribeStorefrontPreview(listener) {
  previewState.listeners.add(listener);
  return () => previewState.listeners.delete(listener);
}

function mergeTenant(base, override) {
  if (!base && !override) return null;
  const next = { ...(base || {}), ...(override || {}) };
  const key = String(next.companyKey || next.key || "").trim().toLowerCase();
  if (key) {
    next.companyKey = key;
    next.key = key;
  }
  if (override?.storefrontCopy && typeof override.storefrontCopy === "object") {
    next.storefrontCopy = override.storefrontCopy;
  }
  return next;
}

function applyPreviewInit(payload = {}) {
  previewState.jwt = String(payload.jwt || "").trim();
  previewState.companyKey = String(payload.companyKey || "").trim().toLowerCase();
  previewState.locale = String(payload.locale || "en-US").trim() || "en-US";
  previewState.tenantOverride = mergeTenant(payload.tenant || null, payload.override || null);
  previewState.ready = Boolean(previewState.companyKey);

  if (typeof window !== "undefined" && previewState.locale) {
    try {
      localStorage.setItem("vbsTextLocale", previewState.locale);
    } catch {
      // ignore storage errors
    }
  }

  notifyListeners();
}

function applyPreviewPatch(payload = {}) {
  if (payload.locale) {
    previewState.locale = String(payload.locale).trim() || previewState.locale;
    if (typeof window !== "undefined") {
      try {
        localStorage.setItem("vbsTextLocale", previewState.locale);
      } catch {
        // ignore storage errors
      }
    }
  }
  if (payload.override || payload.tenant) {
    previewState.tenantOverride = mergeTenant(
      payload.tenant || previewState.tenantOverride,
      payload.override || payload,
    );
  }
  notifyListeners();
}

function isAllowedParentOrigin(origin) {
  if (!origin || typeof window === "undefined") return false;
  if (!isStorefrontPreviewEmbed()) return false;
  try {
    const parentUrl = new URL(origin);
    const selfUrl = new URL(window.location.href);
    if (parentUrl.origin === selfUrl.origin) return true;
    if (parentUrl.hostname === selfUrl.hostname) return true;

    const allowed = new Set([window.location.origin]);
    if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
      allowed.add("http://localhost:3100");
      allowed.add("http://127.0.0.1:3100");
    }

    if (allowed.has(parentUrl.origin)) return true;

    // Staff editor on a different hostname (e.g. vbspecialties.com/staff)
    // previewing this tenant's public domain (e.g. edsdrive.com).
    return parentUrl.protocol === "https:" || parentUrl.hostname === "localhost";
  } catch {
    return false;
  }
}

function postToParent(message) {
  if (typeof window === "undefined" || window.parent === window) return;
  try {
    window.parent.postMessage(message, "*");
  } catch {
    // ignore postMessage errors
  }
}

export function bootstrapStorefrontPreview() {
  if (typeof window === "undefined") return false;

  const path = window.location.pathname || "";
  previewState.active = isStorefrontPreviewPath(path);
  previewState.embed =
    previewState.active &&
    new URLSearchParams(window.location.search).get("embed") === "1";

  if (!previewState.active) return false;

  window.addEventListener("message", (event) => {
    if (!isAllowedParentOrigin(event.origin)) return;
    const data = event.data;
    if (!data || data.channel !== PREVIEW_MESSAGE) return;

    if (data.type === "init") {
      applyPreviewInit(data.payload || {});
      return;
    }
    if (data.type === "patch") {
      applyPreviewPatch(data.payload || {});
    }
  });

  if (previewState.embed) {
    postToParent({ channel: PREVIEW_MESSAGE, type: "ready" });
  }

  return true;
}

export { PREVIEW_MESSAGE, PREVIEW_PATH_PREFIX };
